import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { notification } from 'antd';

import checkinPadlock from 'assets/images/check-in/checkin-padlock.svg';
import checkinTicket from 'assets/images/check-in/checkin-ticket.svg';
import blinketLogo from 'assets/images/new-blinket-logo.svg';
import QrCodeScanner from 'components/Pages/Admin/CheckinPage/QrCodeScanner';
import { jwtDecode } from 'jwt-decode';
import eventStaffService from 'services/eventStaff';

import { CheckinFaq, CredentialNotValid, InvalidToken, LoadingCredential, NotAvailableYet, Warning } from './Messages';
import styles from './styles.module.css';

interface ICredential {
  event_id: string;
  iat: number;
  staff_id: string;
  token_type: 'owner_access' | 'staff_access';
}

enum AccessStatus {
  Loading = 'loading',
  InvalidToken = 'invalid token',
  Invalid = 'invalid',
  NotAvailable = 'not available',
  Available = 'available'
}

export const CheckinPage = () => {
  const [hasAccess, setHasAccess] = useState<AccessStatus>(AccessStatus.Loading);
  const [openWarning, setOpenWarning] = useState(false);
  const token = new URLSearchParams(useLocation().search).get('token');

  useEffect(() => {
    if (!token) return;
    const credential = jwtDecode<ICredential>(token);

    if (credential.token_type === 'owner_access') {
      setOpenWarning(true);
      return;
    }

    const subscription = eventStaffService.getAccess(token).subscribe({
      next: () => {
        setHasAccess(AccessStatus.Available);
      },
      error: error => {
        const errorStatus = error.response?.status;
        const errorCode = error.response?.data?.code;

        if (errorStatus === 400 && errorCode === 'ERR_CBS400') {
          setHasAccess(AccessStatus.NotAvailable);
        }

        if (errorStatus === 400 && errorCode === 'ERR_CAE400') {
          setHasAccess(AccessStatus.Invalid);
        }

        if (errorStatus === 401) {
          setHasAccess(AccessStatus.InvalidToken);
        }

        if (errorStatus === 403) {
          setHasAccess(AccessStatus.Invalid);
        }

        if (errorStatus === 500) {
          notification.error({
            message: 'Estamos passando por uma instabilidade, tente novamente mais tarde'
          });
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [token]);

  if (hasAccess === 'available') {
    return <QrCodeScanner />;
  }

  return (
    <div className={styles.fullpage}>
      <div className={hasAccess === 'not available' ? `${styles.wrapper} ${styles.spaceBetween}` : styles.wrapper}>
        <div>
          <img src={blinketLogo} alt='Blinket Logo' className={styles.logo} />
        </div>
        {hasAccess === AccessStatus.Loading && !openWarning && (
          <div className={styles.svgContainer}>
            <img src={checkinTicket} className={styles.svg} />
          </div>
        )}
        {hasAccess !== AccessStatus.Loading && (
          <div className={styles.svgContainer}>
            <img src={checkinPadlock} className={styles.svg} />
          </div>
        )}
        {hasAccess === AccessStatus.InvalidToken && <InvalidToken />}
        {hasAccess === AccessStatus.Loading && !openWarning && <LoadingCredential />}
        {hasAccess === AccessStatus.Loading && openWarning && (
          <Warning
            onOk={() => {
              setOpenWarning(false);
              setHasAccess(AccessStatus.Available);
            }}
          />
        )}
        {hasAccess === AccessStatus.Invalid && <CredentialNotValid />}
        {hasAccess === AccessStatus.NotAvailable && (
          <>
            <NotAvailableYet />
            <CheckinFaq />
          </>
        )}
      </div>
    </div>
  );
};
